import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { LocateFixed } from 'lucide-react';
import { Button, Field, Input, Select } from '../../ui';
import { request } from '../../api/client';

/**
 * WHERE A FARMER FARMS — the four answers, asked the same way wherever we ask them.
 *
 * Two screens collect this: `/welcome` (someone who signed in with a number we did not know) and
 * `/register` (someone who came in through "Register as a farmer"). They differ in what surrounds
 * these fields, not in the fields themselves, so the pincode lookup, the district fallback and the
 * location button live here once. Android has the same split — `OnboardingFormFields` is this
 * component's counterpart.
 *
 * Controlled on purpose: the page owns the draft, because only the page knows whether it is
 * autosaving to a signup row or holding the answers until a number has been verified.
 */

export type FarmerPlace = {
  stateCode?: string;
  districtId?: string | null;
  districtText?: string | null;
  village?: string;
  pincode?: string;
  latitude?: number | null;
  longitude?: number | null;
};

export type PlaceErrors = Partial<Record<'state' | 'district' | 'village' | 'pincode', string>>;

const NOT_LISTED = '__other__';

export function FarmerPlaceFields({
  value, onChange, errors, disabled, showLocate = true,
}: {
  value: FarmerPlace;
  onChange: (patch: FarmerPlace) => void;
  errors: PlaceErrors;
  disabled?: boolean;
  /** Off where a device fix would mean nothing — staff filling the form in on a call. */
  showLocate?: boolean;
}) {
  const [otherDistrict, setOtherDistrict] = useState(false);
  const [locating, setLocating] = useState(false);
  const [locNote, setLocNote] = useState<string | null>(null);

  // A resumed draft that holds a TYPED district must open in the typed state, or the answer they
  // already gave is invisible behind a dropdown.
  useEffect(() => {
    if (value.districtText && !value.districtId) setOtherDistrict(true);
  }, [value.districtText, value.districtId]);

  const { data: states = [] } = useQuery({
    queryKey: ['onboarding-states'],
    queryFn: () => request<any[]>({ url: '/onboarding/states' }),
    staleTime: 3600_000,
  });
  const { data: districts = [], isFetching: loadingDistricts } = useQuery({
    queryKey: ['onboarding-districts', value.stateCode],
    queryFn: () => request<any[]>({ url: '/onboarding/districts', params: { stateCode: value.stateCode } }),
    enabled: !!value.stateCode,
    staleTime: 3600_000,
  });
  // A state we hold no districts for can only be answered in words.
  const typeDistrict = otherDistrict || (!!value.stateCode && !loadingDistricts && districts.length === 0);

  // A full pincode fills the state when it is still empty — the postal circle names the state.
  const onPincode = async (v: string) => {
    const pin = v.replace(/\D/g, '').slice(0, 6);
    onChange({ pincode: pin });
    if (pin.length !== 6) return;
    try {
      const r = await request<any>({ url: `/onboarding/pincode/${pin}` });
      if (r?.stateCode && !value.stateCode) onChange({ stateCode: r.stateCode, districtId: null });
      const ours = (r?.districts ?? []).filter((x: any) => x.territoryId);
      if (ours.length === 1 && !value.districtId && (r.stateCode === value.stateCode || !value.stateCode)) {
        setOtherDistrict(false);
        onChange({ districtId: ours[0].territoryId, districtText: null });
      }
    } catch { /* a lookup miss is not an error — they can still choose */ }
  };

  const useMyLocation = () => {
    if (!('geolocation' in navigator)) { setLocNote('This browser cannot share a location. Please choose below.'); return; }
    setLocating(true);
    setLocNote(null);
    navigator.geolocation.getCurrentPosition(async (pos) => {
      const latitude = Number(pos.coords.latitude.toFixed(6));
      const longitude = Number(pos.coords.longitude.toFixed(6));
      onChange({ latitude, longitude });
      try {
        const r = await request<any>({ url: '/onboarding/locate', method: 'POST', data: { latitude, longitude } });
        const s = r?.suggestion;
        if (s) {
          setOtherDistrict(false);
          onChange({ stateCode: s.stateCode, districtId: s.districtId, districtText: null });
          setLocNote(`You seem to be in ${s.districtName} district. Change it below if that is wrong.`);
        } else {
          setLocNote('We could not tell your district from your location. Please choose it below.');
        }
      } catch {
        setLocNote('We could not tell your district from your location. Please choose it below.');
      } finally { setLocating(false); }
    }, () => {
      setLocating(false);
      setLocNote('Location was not shared — that is fine, just choose below.');
    }, { enableHighAccuracy: false, timeout: 15000, maximumAge: 600000 });
  };

  // States we work in lead the list; the rest are still there, because a farmer in a state we do not
  // cover must be able to answer honestly.
  const stateOptions = useMemo(() => {
    const covered = states.filter((s: any) => s.covered);
    const rest = states.filter((s: any) => !s.covered);
    return [
      ...(covered.length ? [{ label: 'Where we work', options: covered.map((s: any) => ({ value: s.code, label: s.name })) }] : []),
      { label: covered.length ? 'Other states' : 'States', options: rest.map((s: any) => ({ value: s.code, label: s.name })) },
    ];
  }, [states]);

  const districtOptions = useMemo(() => [
    ...districts.map((x: any) => ({ value: x.id, label: x.name })),
    { value: NOT_LISTED, label: 'My district is not in this list' },
  ], [districts]);

  return (
    <>
      {showLocate && (
        <div className="rounded-lg border border-dashed border-line p-3">
          <Button icon={<LocateFixed size={16} />} loading={locating} disabled={disabled} onClick={useMyLocation}>
            Use my location
          </Button>
          <p className="mt-2 type-caption text-muted">
            {locNote ?? 'Optional — fills in your state and district for you. You can also choose them yourself.'}
          </p>
        </div>
      )}

      <Field label="State" required error={errors.state}>
        <Select showSearch optionFilterProp="label" placeholder="Choose your state" value={value.stateCode ?? null}
          disabled={disabled} invalid={!!errors.state} options={stateOptions}
          onChange={(v) => { setOtherDistrict(false); onChange({ stateCode: v, districtId: null, districtText: null }); }} />
      </Field>

      <Field label="District" required error={errors.district}
        hint={value.stateCode && !loadingDistricts && districts.length === 0
          ? 'Type your district — we will connect you with the right team.' : undefined}>
        {typeDistrict ? (
          <div className="flex flex-col gap-2">
            <Input value={value.districtText ?? ''} invalid={!!errors.district} maxLength={120} placeholder="Your district"
              disabled={disabled || !value.stateCode} onChange={(e) => onChange({ districtText: e.target.value, districtId: null })} />
            {districts.length > 0 && (
              <button type="button" className="self-start type-caption text-brand-700 underline"
                onClick={() => { setOtherDistrict(false); onChange({ districtText: null }); }}>
                Choose from the list instead
              </button>
            )}
          </div>
        ) : (
          <Select showSearch optionFilterProp="label"
            placeholder={value.stateCode ? 'Choose your district' : 'Choose your state first'}
            disabled={disabled || !value.stateCode} loading={loadingDistricts} value={value.districtId ?? null}
            invalid={!!errors.district} options={districtOptions}
            onChange={(v) => {
              if (v === NOT_LISTED) { setOtherDistrict(true); onChange({ districtId: null }); }
              else onChange({ districtId: v, districtText: null });
            }} />
        )}
      </Field>

      <Field label="Village or town" required htmlFor="ob-village" error={errors.village}>
        <Input id="ob-village" value={value.village ?? ''} invalid={!!errors.village} maxLength={120}
          disabled={disabled} placeholder="e.g. Ozar" onChange={(e) => onChange({ village: e.target.value })} />
      </Field>

      <Field label="Pincode" required htmlFor="ob-pin" error={errors.pincode}>
        <Input id="ob-pin" inputMode="numeric" autoComplete="postal-code" value={value.pincode ?? ''}
          invalid={!!errors.pincode} disabled={disabled} placeholder="6 digits" maxLength={6}
          className="max-w-40" onChange={(e) => onPincode(e.target.value)} />
      </Field>
    </>
  );
}
